import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Grid,
  Stack,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import StockAnalysis from './StockAnalysis';
import {
  Search,
  Inventory,
  Clear,
  Add,
  DeleteOutline,
  ShoppingCartOutlined,
  Refresh,
  TrendingUp,
  LocalPharmacy,
  AttachMoney,
  CheckCircle,
  Event,
  WarningAmber,
  ReceiptLong,
  LockOutlined,
  Close,
} from '@mui/icons-material';
import { loadInventory, processPurchase, loadProductsForPurchase, deleteInventory } from '../BaseURL';
import axios from 'axios';
import Swal from 'sweetalert2';

// ── Theme tokens (mirrors Home.js) ─────────────────────────────────────────
const PRIMARY      = '#41a386';
const PRIMARY_DARK = '#2f8068';
const PRIMARY_BG   = 'rgba(65,163,134,0.10)';
const BORDER       = '#e5e9ef';
const MUTED        = '#64748b';
const STRONG       = '#0f172a';
const LABEL_SX = {
  fontSize: '0.66rem', fontWeight: 700, color: '#94a3b8',
  textTransform: 'uppercase', letterSpacing: '0.6px',
};

const PurchaseOrders = () => {
  const [activeTab, setActiveTab] = useState(0);

  const [inventoryData, setInventoryData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Purchase dialog states
  const [openPurchaseDialog, setOpenPurchaseDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [purchaseQuantity, setPurchaseQuantity] = useState('');
  const [purchaseCost, setPurchaseCost] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [purchaseItems, setPurchaseItems] = useState([]);
  const [supplierName, setSupplierName] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [savingPurchase, setSavingPurchase] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productTypeFilter, setProductTypeFilter] = useState('all');
  const [purchaseProducts, setPurchaseProducts] = useState([]);
  const [loadingPurchaseProducts, setLoadingPurchaseProducts] = useState(false);

  const quantityRef = useRef(null);

  // Filtered products for purchase dialog
  const filteredPurchaseProducts = purchaseProducts.filter(product => {
    const matchSearch =
      product.name?.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
      product.code?.toLowerCase().includes(productSearchTerm.toLowerCase());
    const matchType = productTypeFilter === 'all' || product.type === productTypeFilter;
    return matchSearch && matchType;
  });

  // ── Helpers ────────────────────────────────────────────────────────────────

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(amount || 0);

  const isExpiringSoon = (dateStr) => {
    if (!dateStr) return false;
    const diff = (new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24);
    return diff >= 0 && diff <= 90;
  };

  const isExpired = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
  };

  const expiryStatus = (dateStr) => {
    if (!dateStr) return { label: 'No expiry', bg: '#f1f5f9', color: MUTED };
    if (isExpired(dateStr))
      return { label: `Expired ${new Date(dateStr).toLocaleDateString()}`, bg: 'rgba(220,38,38,0.10)', color: '#991B1B' };
    if (isExpiringSoon(dateStr))
      return { label: `Exp ${new Date(dateStr).toLocaleDateString()}`, bg: 'rgba(217,119,6,0.10)', color: '#A16207' };
    return { label: `Exp ${new Date(dateStr).toLocaleDateString()}`, bg: 'rgba(16,185,129,0.10)', color: '#15803D' };
  };

  // Today's date string for min-date validation
  const today = new Date().toISOString().split('T')[0];

  // Determine if a row's stock can still be reversed (deleted)
  const canDeleteRow = (item) => {
    const stock = parseInt(item.stock || 0);
    const balance = parseInt(item.currentBalance ?? 0);
    return balance >= stock;
  };

  // ── Data Loading ──────────────────────────────────────────────────────────

  const loadInventoryData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(loadInventory);
      if (response.data.success) {
        setInventoryData(response.data.results);
        setFilteredData(response.data.results);
      } else {
        throw new Error('Failed to load inventory data');
      }
    } catch (error) {
      console.error('Error loading inventory:', error);
      setError(error.message || 'Failed to load inventory data');
    } finally {
      setLoading(false);
    }
  };

  const loadPurchaseProducts = async () => {
    try {
      setLoadingPurchaseProducts(true);
      const response = await axios.get(loadProductsForPurchase);
      if (response.data.success) setPurchaseProducts(response.data.results);
    } catch (error) {
      console.error('Error loading purchase products:', error);
    } finally {
      setLoadingPurchaseProducts(false);
    }
  };

  useEffect(() => {
    loadInventoryData();
    loadPurchaseProducts();
  }, []);

  // ── Filters ───────────────────────────────────────────────────────────────

  useEffect(() => {
    let filtered = [...inventoryData];
    if (searchTerm.trim()) {
      filtered = filtered.filter(item =>
        item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.dat?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (startDate) {
      filtered = filtered.filter(item => new Date(item.dat) >= new Date(startDate));
    }
    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      filtered = filtered.filter(item => new Date(item.dat) <= end);
    }
    if (statusFilter === 'active') {
      filtered = filtered.filter(item => !(String(item.is_deleted) === '1' || item.is_deleted === true));
    } else if (statusFilter === 'deleted') {
      filtered = filtered.filter(item => String(item.is_deleted) === '1' || item.is_deleted === true);
    }
    setFilteredData(filtered);
    setPage(0);
  }, [searchTerm, startDate, endDate, statusFilter, inventoryData]);

  const clearAllFilters = () => { setSearchTerm(''); setStartDate(''); setEndDate(''); setStatusFilter('all'); };
  const hasActiveFilters = () => searchTerm || startDate || endDate || statusFilter !== 'all';

  // ── Delete ────────────────────────────────────────────────────────────────

  const handleDeleteInventoryItem = async (item) => {
    if (!canDeleteRow(item)) {
      Swal.fire({
        title: 'Cannot Delete',
        html: `<div style="text-align:left;padding:8px 0">
          <div style="background:#fef3c7;border:1px solid #fcd34d;border-radius:8px;padding:12px 16px">
            <strong style="color:#92400e">${item.name}</strong> has already been partly issued from stock.<br/>
            <span style="color:#64748b;font-size:0.84rem">
              Recorded: <strong>${item.stock}</strong> units · Available now: <strong>${item.currentBalance ?? 0}</strong> units
            </span>
          </div>
          <p style="margin:10px 0 0;color:#6b7280;font-size:0.84rem">
            Reversing this invoice would put the balance below zero. Adjust stock manually if a correction is needed.
          </p>
        </div>`,
        icon: 'info', confirmButtonColor: PRIMARY, confirmButtonText: 'OK',
      });
      return;
    }

    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    const userRole = userInfo.role     || '';
    const username = userInfo.username || '';

    if (userRole !== 'System Administrator') {
      Swal.fire({
        title: 'Access Denied',
        html: `<div style="text-align:center;padding:8px 0">
          <p style="color:#374151;margin-bottom:8px">Only a <strong>System Administrator</strong> can delete inventory items.</p>
          <p style="color:#9ca3af;font-size:0.85rem;margin:0">Your role: <strong style="color:#ef4444">${userRole || 'Unknown'}</strong></p>
        </div>`,
        icon: 'error', confirmButtonColor: PRIMARY, confirmButtonText: 'OK',
      });
      return;
    }

    const escapeHtml = (s) => String(s ?? '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    const confirm = await Swal.fire({
      title: 'Delete Inventory Item?',
      html: `<div style="text-align:left;padding:4px 0">
        <div style="background:#fef2f2;border:1px solid #fecaca;border-radius:8px;padding:12px 16px;margin-bottom:10px">
          <strong style="color:#1e293b">${escapeHtml(item.name)}</strong><br/>
          <span style="color:#64748b;font-size:0.84rem">Stock: <strong>${item.stock || 0}</strong> units · Date: ${escapeHtml(item.dat)}</span>
        </div>
        <p style="margin:0;color:#6b7280;font-size:0.84rem">The row will be <span style="color:#ef4444;text-decoration:line-through;font-weight:600">struck through in red</span> and stock removed from balance.</p>
      </div>`,
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#d32f2f', cancelButtonColor: '#6b7280',
      confirmButtonText: 'Continue →', cancelButtonText: 'Cancel', reverseButtons: true,
    });
    if (!confirm.isConfirmed) return;

    const { value: password } = await Swal.fire({
      title: 'Enter Administrator Password',
      html: `<div style="margin-bottom:4px">
        <div style="display:inline-flex;align-items:center;gap:8px;background:#f1f5f9;border-radius:20px;padding:6px 14px;margin-bottom:12px">
          <span>👤</span>
          <span style="color:#374151;font-size:0.88rem;font-weight:600">${username}</span>
          <span style="background:#dcfce7;color:#166534;font-size:0.7rem;font-weight:700;padding:2px 8px;border-radius:10px">System Administrator</span>
        </div>
        <p style="color:#9ca3af;font-size:0.82rem;margin:0 0 4px">Enter your password to authorise this action.</p>
      </div>
      <input id="swal-del-pwd" type="password" class="swal2-input" placeholder="Password"
        style="width:82%;margin:8px auto 0" autocomplete="current-password"/>`,
      showCancelButton: true,
      confirmButtonColor: '#d32f2f', cancelButtonColor: '#6b7280',
      confirmButtonText: 'Confirm Delete', cancelButtonText: 'Cancel', reverseButtons: true,
      focusConfirm: false,
      didOpen: () => {
        const inp = document.getElementById('swal-del-pwd');
        inp.focus();
        inp.addEventListener('keydown', (e) => { if (e.key === 'Enter') Swal.clickConfirm(); });
      },
      preConfirm: () => {
        const pwd = document.getElementById('swal-del-pwd').value;
        if (!pwd || pwd.trim() === '') { Swal.showValidationMessage('⚠ Password cannot be empty'); return false; }
        return pwd;
      },
    });
    if (!password) return;

    try {
      const response = await axios.delete(deleteInventory(item.trans_code), {
        data: { password, username },
      });
      if (response.data.success) {
        Swal.fire({ title: 'Deleted!', text: `"${item.name}" has been marked as deleted.`, icon: 'success', timer: 2000, showConfirmButton: false });
        loadInventoryData();
      } else {
        throw new Error(response.data.message || 'Failed to delete item');
      }
    } catch (error) {
      Swal.fire({
        title: error.response?.status === 401 ? 'Wrong Password' : 'Error!',
        text: error.response?.data?.message || error.message || 'Something went wrong.',
        icon: 'error', confirmButtonColor: PRIMARY,
      });
    }
  };

  // ── Purchase Dialog ───────────────────────────────────────────────────────

  const handleOpenPurchaseDialog = () => {
    setOpenPurchaseDialog(true);
    setSelectedProduct(null);
    setPurchaseQuantity('');
    setPurchaseCost('');
    setExpirationDate('');
    setProductSearchTerm('');
    setProductTypeFilter('all');
    setSupplierName('');
    setInvoiceNumber('');
  };

  const handleClosePurchaseDialog = () => {
    setOpenPurchaseDialog(false);
    setSelectedProduct(null);
    setPurchaseQuantity('');
    setPurchaseCost('');
    setExpirationDate('');
    setPurchaseItems([]);
    setProductSearchTerm('');
    setProductTypeFilter('all');
    setSupplierName('');
    setInvoiceNumber('');
  };

  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setPurchaseQuantity('');
    setPurchaseCost(product.unitPrice ? product.unitPrice.toString() : '');
    setExpirationDate('');
    setTimeout(() => quantityRef.current?.focus(), 50);
  };

  const handleAddPurchaseItem = () => {
    if (!selectedProduct || !purchaseQuantity || !purchaseCost || !expirationDate) {
      Swal.fire({
        icon: 'error',
        title: 'Missing Information',
        text: 'Please fill in quantity, unit cost, and expiration date.',
        toast: true, position: 'top-end', timer: 3000, showConfirmButton: false,
      });
      return;
    }

    const quantity = parseInt(purchaseQuantity);
    const cost = parseFloat(purchaseCost);

    if (quantity <= 0 || cost <= 0) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Values',
        text: 'Quantity and cost must be greater than 0.',
        toast: true, position: 'top-end', timer: 3000, showConfirmButton: false,
      });
      return;
    }

    setPurchaseItems(prev => [...prev, {
      ...selectedProduct,
      purchaseQuantity: quantity,
      purchaseCost: cost,
      expirationDate,
      totalCost: quantity * cost,
    }]);

    Swal.fire({
      title: 'Added',
      text: `${selectedProduct.name} added.`,
      icon: 'success', timer: 1200, showConfirmButton: false, toast: true, position: 'top-end',
    });

    setPurchaseQuantity('');
    setPurchaseCost(selectedProduct.unitPrice ? selectedProduct.unitPrice.toString() : '');
    setExpirationDate('');
    setTimeout(() => quantityRef.current?.focus(), 50);
  };

  const handleFormKeyDown = (e) => {
    if (e.key === 'Enter' && selectedProduct && purchaseQuantity && purchaseCost && expirationDate) {
      e.preventDefault();
      handleAddPurchaseItem();
    }
  };

  const handleRemovePurchaseItem = (index) => {
    setPurchaseItems(purchaseItems.filter((_, i) => i !== index));
  };

  const handleSavePurchase = async () => {
    if (purchaseItems.length === 0) {
      Swal.fire({ title: 'Error!', text: 'Please add at least one item.', icon: 'error' });
      return;
    }

    const itemsHtml = purchaseItems
      .map(it => `<tr>
        <td style="padding:4px 8px;text-align:left">${it.name}</td>
        <td style="padding:4px 8px;text-align:right">${it.purchaseQuantity}</td>
        <td style="padding:4px 8px;text-align:right">${formatCurrency(it.totalCost)}</td>
      </tr>`).join('');

    const total = purchaseItems.reduce((sum, it) => sum + it.totalCost, 0);

    const result = await Swal.fire({
      title: 'Confirm Purchase',
      html: `<div style="text-align:left;font-size:0.88rem">
        <table style="width:100%;border-collapse:collapse;margin:6px 0">
          <thead><tr style="background:#f1f5f9;color:#475569">
            <th style="padding:6px 8px;text-align:left">Product</th>
            <th style="padding:6px 8px;text-align:right">Qty</th>
            <th style="padding:6px 8px;text-align:right">Total</th>
          </tr></thead>
          <tbody>${itemsHtml}</tbody>
          <tfoot><tr style="font-weight:700;color:#0f172a;border-top:2px solid #e2e8f0">
            <td colspan="2" style="padding:8px;text-align:right">Grand Total</td>
            <td style="padding:8px;text-align:right">${formatCurrency(total)}</td>
          </tr></tfoot>
        </table>
      </div>`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: PRIMARY,
      cancelButtonColor: '#d32f2f',
      confirmButtonText: 'Yes, Save Invoice',
      width: 600,
    });
    if (!result.isConfirmed) return;

    setSavingPurchase(true);
    try {
      const purchaseData = {
        items: purchaseItems,
        supplier: supplierName || null,
        invoiceNumber: invoiceNumber || null,
        purchaseDate: new Date().toISOString().split('T')[0],
      };
      const response = await axios.post(processPurchase, purchaseData);
      if (response.data.success) {
        Swal.fire({ title: 'Success!', text: `Purchase processed for ${purchaseItems.length} item(s)!`, icon: 'success', timer: 2000, showConfirmButton: false });
        handleClosePurchaseDialog();
        loadInventoryData();
        loadPurchaseProducts();
      } else {
        throw new Error(response.data.message || 'Failed to process purchase');
      }
    } catch (error) {
      Swal.fire({ title: 'Error!', text: 'Error: ' + (error.response?.data?.message || error.message), icon: 'error' });
    } finally {
      setSavingPurchase(false);
    }
  };

  // ── Dashboard Metrics ─────────────────────────────────────────────────────

  const dashboardMetrics = useMemo(() => {
    const totalProducts = filteredData.length;
    const totalStock = filteredData.reduce((sum, item) => sum + parseInt(item.stock || 0), 0);
    const totalValue = filteredData.reduce((sum, item) =>
      sum + parseFloat(item.pricing || 0) * parseInt(item.stock || 0), 0);
    const expiringSoon = filteredData.filter(i => isExpiringSoon(i.expiry_date) && !isExpired(i.expiry_date)).length;
    return { totalProducts, totalStock, totalValue, expiringSoon };
  }, [filteredData]);

  const grandTotal = purchaseItems.reduce((sum, item) => sum + item.totalCost, 0);
  const totalUnits = purchaseItems.reduce((s, it) => s + it.purchaseQuantity, 0);

  // ── Render Guards ─────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <CircularProgress size={48} sx={{ color: PRIMARY }} />
        <Typography sx={{ mt: 2, fontSize: '0.9rem', color: MUTED, fontWeight: 500 }}>Loading inventory…</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', px: 3 }}>
        <Alert severity="error" sx={{ maxWidth: 500, width: '100%', borderRadius: 2 }}>{error}</Alert>
      </Box>
    );
  }

  // ── Main Render ───────────────────────────────────────────────────────────

  return (
    <Box sx={{ flexGrow: 1, bgcolor: '#f8fafc', minHeight: '100vh', py: 2.5 }}>
      <Container maxWidth="xl">

        {/* Header */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={1.5}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography sx={{ ...LABEL_SX, color: PRIMARY, mb: 0.25 }}>
              Procurement · Purchase Orders
            </Typography>
            <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: STRONG, lineHeight: 1.2 }}>
              Inventory Purchases
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: MUTED, mt: 0.25 }}>
              {inventoryData.length} purchase{inventoryData.length === 1 ? '' : 's'} on file ·
              {' '}{filteredData.length} matching current filters
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh data" arrow>
              <IconButton
                onClick={loadInventoryData}
                size="small"
                sx={{
                  border: `1px solid ${BORDER}`, bgcolor: '#fff', color: MUTED,
                  '&:hover': { bgcolor: '#f1f5f9', color: STRONG },
                }}
              >
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<ReceiptLong />}
              onClick={handleOpenPurchaseDialog}
              sx={{
                bgcolor: PRIMARY,
                '&:hover': { bgcolor: PRIMARY_DARK },
                textTransform: 'none', fontWeight: 700, px: 2.5,
              }}
            >
              New Purchase Order
            </Button>
          </Stack>
        </Stack>

        {/* Stats strip — 4 compact tiles */}
        <Grid container spacing={1.25} sx={{ mb: 2 }}>
          {[
            { label: 'Total Records',   value: dashboardMetrics.totalProducts.toLocaleString(),         icon: <Inventory   fontSize="small" />, tint: '#f1f5f9',                color: STRONG    },
            { label: 'Total Stock',     value: dashboardMetrics.totalStock.toLocaleString(),            icon: <TrendingUp  fontSize="small" />, tint: 'rgba(16,185,129,0.10)',  color: '#15803D' },
            { label: 'Inventory Value', value: formatCurrency(dashboardMetrics.totalValue),             icon: <AttachMoney fontSize="small" />, tint: 'rgba(217,119,6,0.10)',   color: '#A16207' },
            { label: 'Expiring ≤ 90d',  value: dashboardMetrics.expiringSoon.toLocaleString(),          icon: <WarningAmber fontSize="small" />, tint: 'rgba(220,38,38,0.10)',  color: '#991B1B' },
          ].map(t => (
            <Grid item xs={6} md={3} key={t.label}>
              <Paper elevation={0} sx={{
                p: 1.5, borderRadius: 2,
                border: `1px solid ${BORDER}`, bgcolor: '#fff',
                display: 'flex', alignItems: 'center', gap: 1.5,
              }}>
                <Box sx={{
                  width: 36, height: 36, borderRadius: 1.5,
                  bgcolor: t.tint, color: t.color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  {t.icon}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={LABEL_SX}>{t.label}</Typography>
                  <Typography sx={{ fontSize: '1.25rem', fontWeight: 800, color: STRONG, lineHeight: 1.1, mt: 0.25 }} noWrap>
                    {t.value}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Tabs */}
        <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, mb: 2, bgcolor: '#fff' }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{
              px: 1,
              minHeight: 44,
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontSize: '0.82rem', minHeight: 44, color: MUTED },
              '& .Mui-selected': { color: PRIMARY },
              '& .MuiTabs-indicator': { bgcolor: PRIMARY, height: 2 },
            }}
          >
            <Tab label="Inventory Purchases" icon={<Inventory sx={{ fontSize: 16 }} />} iconPosition="start" />
            <Tab label="Stock Intelligence"  icon={<TrendingUp sx={{ fontSize: 16 }} />} iconPosition="start" />
          </Tabs>
        </Paper>

        {/* Tab 0: Inventory list */}
        {activeTab === 0 && (
          <Paper elevation={0} sx={{
            border: `1px solid ${BORDER}`, borderRadius: 2, overflow: 'hidden', bgcolor: '#fff',
          }}>
            {/* Filter row — compact, single line on desktop */}
            <Box sx={{
              p: 1.5,
              borderBottom: `1px solid ${BORDER}`,
              display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1,
              bgcolor: '#fff',
            }}>
              <TextField
                size="small"
                placeholder="Search by product, code or date…"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                sx={{ minWidth: 240, flex: { xs: '1 1 100%', md: '0 0 320px' } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" sx={{ color: '#94a3b8' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                size="small" type="date" label="From"
                value={startDate} onChange={e => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 150 }}
              />
              <TextField
                size="small" type="date" label="To"
                value={endDate} onChange={e => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 150 }}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="deleted">Deleted</MenuItem>
                </Select>
              </FormControl>
              {hasActiveFilters() && (
                <Button size="small" onClick={clearAllFilters} startIcon={<Clear sx={{ fontSize: 16 }} />}
                  sx={{ textTransform: 'none', color: MUTED, fontSize: '0.78rem', '&:hover': { bgcolor: '#f1f5f9' } }}>
                  Clear
                </Button>
              )}
              <Box sx={{ flex: 1 }} />
              <Typography sx={{ fontSize: '0.74rem', color: MUTED }}>
                {filteredData.length} of {inventoryData.length} shown
              </Typography>
            </Box>

            {/* Table */}
            <TableContainer sx={{ maxHeight: '60vh' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { bgcolor: '#f8fafc', borderBottom: `1px solid ${BORDER}`, ...LABEL_SX, py: 1.25 } }}>
                    <TableCell sx={{ minWidth: 100 }}>Date</TableCell>
                    <TableCell sx={{ minWidth: 220 }}>Product</TableCell>
                    <TableCell align="center" sx={{ width: 110 }}>Type</TableCell>
                    <TableCell align="center" sx={{ width: 90 }}>Stock</TableCell>
                    <TableCell align="right"  sx={{ width: 110 }}>Unit price</TableCell>
                    <TableCell align="right"  sx={{ width: 130 }}>Total value</TableCell>
                    <TableCell align="center" sx={{ width: 170 }}>Expiry</TableCell>
                    <TableCell align="right"  sx={{ width: 80 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item, index) => {
                    const unitPrice = parseFloat(item.pricing || 0);
                    const stock     = parseInt(item.stock || 0);
                    const exp       = expiryStatus(item.expiry_date);
                    const isDeleted = String(item.is_deleted) === '1' || item.is_deleted === true;
                    const deletable = canDeleteRow(item);
                    const dispensed = stock - parseInt(item.currentBalance ?? 0);
                    const isDrug    = item.type === 'drug';
                    const strike    = isDeleted ? { textDecoration: 'line-through', color: '#ef4444' } : {};

                    return (
                      <TableRow
                        key={index}
                        sx={{
                          bgcolor: isDeleted ? 'rgba(220,38,38,0.04)' : 'transparent',
                          '&:hover': { bgcolor: isDeleted ? 'rgba(220,38,38,0.07)' : '#f8fafc' },
                          borderLeft: isDeleted ? '3px solid #ef4444' : '3px solid transparent',
                        }}
                      >
                        <TableCell sx={{ color: MUTED, fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums', ...strike }}>
                          {item.dat}
                        </TableCell>

                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={0.75}>
                            <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: STRONG, lineHeight: 1.25, ...strike }}>
                              {item.name}
                            </Typography>
                            {isDeleted && (
                              <Box sx={{
                                px: 0.75, py: 0.15, borderRadius: 0.75,
                                bgcolor: 'rgba(220,38,38,0.10)', color: '#991B1B',
                                fontSize: '0.62rem', fontWeight: 700,
                                textTransform: 'uppercase', letterSpacing: '0.4px',
                              }}>
                                Deleted
                              </Box>
                            )}
                          </Stack>
                          {item.code && (
                            <Typography sx={{ fontSize: '0.7rem', color: MUTED, mt: 0.25 }}>
                              Code: {item.code}
                            </Typography>
                          )}
                        </TableCell>

                        <TableCell align="center">
                          <Chip
                            icon={isDrug ? <LocalPharmacy sx={{ fontSize: 14 }} /> : <Inventory sx={{ fontSize: 14 }} />}
                            label={item.type ? item.type.charAt(0).toUpperCase() + item.type.slice(1) : '—'}
                            size="small"
                            sx={{
                              height: 22, fontSize: '0.7rem', fontWeight: 700,
                              bgcolor: isDrug ? 'rgba(14,165,233,0.10)' : 'rgba(245,158,11,0.10)',
                              color:   isDrug ? '#0369A1' : '#92400E',
                              '& .MuiChip-icon': { color: 'inherit' },
                            }}
                          />
                        </TableCell>

                        <TableCell align="center">
                          <Typography sx={{
                            fontSize: '0.92rem', fontWeight: 800, color: isDeleted ? '#ef4444' : PRIMARY,
                            fontVariantNumeric: 'tabular-nums', ...strike,
                          }}>
                            {stock.toLocaleString()}
                          </Typography>
                        </TableCell>

                        <TableCell align="right" sx={{ fontSize: '0.84rem', fontWeight: 600, color: STRONG, fontVariantNumeric: 'tabular-nums', ...strike }}>
                          {formatCurrency(unitPrice)}
                        </TableCell>

                        <TableCell align="right" sx={{ fontSize: '0.84rem', fontWeight: 700, color: STRONG, fontVariantNumeric: 'tabular-nums', ...strike }}>
                          {formatCurrency(unitPrice * stock)}
                        </TableCell>

                        <TableCell align="center">
                          <Box sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.5,
                            px: 1, py: 0.25, borderRadius: 1,
                            bgcolor: exp.bg, color: exp.color,
                            fontSize: '0.7rem', fontWeight: 700,
                            opacity: isDeleted ? 0.5 : 1,
                          }}>
                            {exp.label}
                          </Box>
                        </TableCell>

                        <TableCell align="right">
                          {!isDeleted && (
                            deletable ? (
                              <Tooltip title="Delete invoice (reverses stock)" arrow>
                                <IconButton size="small" onClick={() => handleDeleteInventoryItem(item)}
                                  sx={{ color: '#dc2626', '&:hover': { bgcolor: 'rgba(220,38,38,0.10)' } }}>
                                  <DeleteOutline sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip
                                arrow
                                title={
                                  <Box sx={{ p: 0.25 }}>
                                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, display: 'block' }}>
                                      Cannot delete
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.7rem', display: 'block', opacity: 0.85 }}>
                                      {dispensed} unit{dispensed === 1 ? '' : 's'} already dispensed
                                    </Typography>
                                  </Box>
                                }
                              >
                                <span>
                                  <IconButton size="small" disabled sx={{ color: '#cbd5e1' }}>
                                    <LockOutlined sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={filteredData.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{
                borderTop: `1px solid ${BORDER}`,
                '.MuiTablePagination-toolbar': { minHeight: 44 },
                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                  fontSize: '0.78rem', color: MUTED,
                },
                '.MuiTablePagination-select': { fontSize: '0.78rem', fontWeight: 700, color: STRONG },
              }}
            />

            {filteredData.length === 0 && (
              <Box sx={{ p: 5, textAlign: 'center', borderTop: `1px solid ${BORDER}` }}>
                <Inventory sx={{ fontSize: 36, color: '#cbd5e1', mb: 1 }} />
                <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: STRONG }}>
                  No purchases found
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: MUTED, mt: 0.25 }}>
                  {hasActiveFilters() ? 'Try clearing filters or adjusting the date range.' : 'Record a new purchase order to see it here.'}
                </Typography>
              </Box>
            )}
          </Paper>
        )}

        {/* Tab 1: Stock Intelligence */}
        {activeTab === 1 && <StockAnalysis embedded />}
      </Container>

      {/* ── Purchase Dialog ──────────────────────────────────────────────── */}
      <Dialog
        open={openPurchaseDialog} onClose={handleClosePurchaseDialog}
        maxWidth={false} fullWidth
        PaperProps={{ sx: { height: '95vh', width: '95vw', maxWidth: 'none', borderRadius: 3, overflow: 'hidden' } }}
      >
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, py: 1.75, px: 2.5,
          bgcolor: PRIMARY, color: 'white',
        }}>
          <ReceiptLong sx={{ fontSize: 22 }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.2 }}>
              New Purchase Order
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', opacity: 0.9 }}>
              Capture a supplier invoice — products, quantities & batch expiry
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} sx={{ minWidth: { xs: 0, md: 360 } }}>
            <TextField
              size="small" placeholder="Supplier"
              value={supplierName} onChange={e => setSupplierName(e.target.value)}
              sx={{
                bgcolor: 'rgba(255,255,255,0.95)', borderRadius: 1.5,
                '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.82rem' },
                '& .MuiOutlinedInput-input': { py: 0.75 },
              }}
            />
            <TextField
              size="small" placeholder="Invoice #"
              value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)}
              sx={{
                bgcolor: 'rgba(255,255,255,0.95)', borderRadius: 1.5,
                '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.82rem' },
                '& .MuiOutlinedInput-input': { py: 0.75 },
                width: 130,
              }}
            />
          </Stack>
          <IconButton onClick={handleClosePurchaseDialog} disabled={savingPurchase} sx={{ color: 'white' }} size="small">
            <Close sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0, display: 'flex', height: 'calc(100% - 110px)', bgcolor: '#f8fafc' }}>

          {/* Left – Product picker */}
          <Box sx={{ width: '34%', borderRight: `1px solid ${BORDER}`, p: 1.75, bgcolor: '#fff', display: 'flex', flexDirection: 'column' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
              <Typography sx={{ ...LABEL_SX, color: PRIMARY }}>Catalogue</Typography>
              <Typography sx={{ fontSize: '0.74rem', color: MUTED }}>
                {filteredPurchaseProducts.length} item{filteredPurchaseProducts.length === 1 ? '' : 's'}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1} sx={{ mb: 1.25 }}>
              <TextField
                fullWidth size="small" placeholder="Search by name…"
                value={productSearchTerm} onChange={e => setProductSearchTerm(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ color: '#94a3b8', fontSize: 18 }} /></InputAdornment> }}
              />
              <FormControl size="small" sx={{ minWidth: 110 }}>
                <Select value={productTypeFilter} onChange={e => setProductTypeFilter(e.target.value)}>
                  <MenuItem value="all">All types</MenuItem>
                  <MenuItem value="drug">Drugs</MenuItem>
                  <MenuItem value="consumable">Consumables</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            <Box sx={{ flexGrow: 1, overflow: 'auto', borderRadius: 1.5, border: `1px solid ${BORDER}` }}>
              {loadingPurchaseProducts ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                  <CircularProgress sx={{ color: PRIMARY }} size={28} />
                </Box>
              ) : filteredPurchaseProducts.length === 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, color: '#94a3b8' }}>
                  <Search sx={{ fontSize: 36, mb: 0.75 }} />
                  <Typography sx={{ fontSize: '0.82rem' }}>No products found</Typography>
                </Box>
              ) : (
                filteredPurchaseProducts.map((product, idx) => {
                  const isSel = selectedProduct?.name === product.name;
                  const isDrug = product.type === 'drug';
                  const stockNum = product.currentStock ?? 0;
                  return (
                    <Box
                      key={product.name || idx}
                      onClick={() => handleSelectProduct(product)}
                      sx={{
                        px: 1.25, py: 1,
                        borderBottom: `1px solid ${BORDER}`,
                        cursor: 'pointer',
                        bgcolor: isSel ? PRIMARY_BG : '#fff',
                        borderLeft: `3px solid ${isSel ? PRIMARY : 'transparent'}`,
                        '&:hover': { bgcolor: isSel ? PRIMARY_BG : '#f8fafc' },
                        transition: 'background-color 0.12s',
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={0.75}>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography sx={{ fontWeight: 700, color: STRONG, fontSize: '0.84rem', lineHeight: 1.25 }} noWrap>
                            {product.name}
                          </Typography>
                          <Stack direction="row" spacing={0.5} sx={{ mt: 0.4, flexWrap: 'wrap', gap: 0.4 }}>
                            <Box sx={{
                              px: 0.6, py: 0.05, borderRadius: 0.6, fontSize: '0.62rem', fontWeight: 700,
                              textTransform: 'uppercase', letterSpacing: '0.4px',
                              bgcolor: isDrug ? 'rgba(14,165,233,0.10)' : 'rgba(245,158,11,0.10)',
                              color:   isDrug ? '#0369A1' : '#92400E',
                            }}>
                              {product.type || '—'}
                            </Box>
                            <Box sx={{
                              px: 0.6, py: 0.05, borderRadius: 0.6, fontSize: '0.62rem', fontWeight: 700,
                              textTransform: 'uppercase', letterSpacing: '0.4px',
                              bgcolor: stockNum === 0 ? 'rgba(220,38,38,0.10)' : 'rgba(16,185,129,0.10)',
                              color:   stockNum === 0 ? '#991B1B' : '#15803D',
                            }}>
                              Stock {stockNum}
                            </Box>
                          </Stack>
                        </Box>
                        <Typography sx={{ fontWeight: 800, color: PRIMARY, fontSize: '0.78rem', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                          {formatCurrency(product.unitPrice)}
                        </Typography>
                      </Stack>
                    </Box>
                  );
                })
              )}
            </Box>
          </Box>

          {/* Right – Invoice */}
          <Box sx={{ width: '66%', p: 2, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

            {/* Compact entry strip */}
            <Paper elevation={0} sx={{
              p: 1.75, mb: 1.5, borderRadius: 2,
              border: `1px solid ${selectedProduct ? PRIMARY : BORDER}`,
              bgcolor: '#fff',
            }}>
              {selectedProduct ? (
                <>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
                    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0, flex: 1 }}>
                      <Box sx={{
                        width: 32, height: 32, borderRadius: 1.25,
                        bgcolor: PRIMARY_BG, color: PRIMARY,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }}>
                        <LocalPharmacy sx={{ fontSize: 18 }} />
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={LABEL_SX}>Selected Product</Typography>
                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: STRONG, lineHeight: 1.2 }} noWrap>
                          {selectedProduct.name}
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack direction="row" spacing={2} sx={{ flexShrink: 0, pl: 2 }}>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography sx={LABEL_SX}>In stock</Typography>
                        <Typography sx={{ fontSize: '0.92rem', fontWeight: 800, color: STRONG, lineHeight: 1.1 }}>
                          {(selectedProduct.currentStock ?? 0).toLocaleString()}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography sx={LABEL_SX}>Last unit</Typography>
                        <Typography sx={{ fontSize: '0.92rem', fontWeight: 800, color: PRIMARY, lineHeight: 1.1 }}>
                          {formatCurrency(selectedProduct.unitPrice)}
                        </Typography>
                      </Box>
                    </Stack>
                  </Stack>

                  <Grid container spacing={1.25} onKeyDown={handleFormKeyDown}>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        inputRef={quantityRef}
                        fullWidth size="small" label="Quantity" type="number"
                        value={purchaseQuantity} onChange={e => setPurchaseQuantity(e.target.value)}
                        inputProps={{ min: 1 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth size="small" label="Unit Cost (GHS)" type="number"
                        value={purchaseCost} onChange={e => setPurchaseCost(e.target.value)}
                        inputProps={{ min: 0, step: 0.01 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth size="small" label="Expiry Date" type="date"
                        value={expirationDate} onChange={e => setExpirationDate(e.target.value)}
                        inputProps={{ min: today }}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ startAdornment: <InputAdornment position="start"><Event sx={{ color: PRIMARY, fontSize: 16 }} /></InputAdornment> }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Button
                        fullWidth variant="contained" startIcon={<Add />} onClick={handleAddPurchaseItem}
                        disabled={!purchaseQuantity || !purchaseCost || !expirationDate}
                        sx={{
                          height: 40,
                          bgcolor: PRIMARY, '&:hover': { bgcolor: PRIMARY_DARK },
                          textTransform: 'none', fontWeight: 700, boxShadow: 'none',
                        }}
                      >
                        Add line ⏎
                      </Button>
                    </Grid>
                  </Grid>

                  {expirationDate && (
                    <Box sx={{ mt: 1.25 }}>
                      {isExpired(expirationDate) ? (
                        <Stack direction="row" alignItems="center" spacing={0.75}
                          sx={{ p: 0.75, bgcolor: 'rgba(220,38,38,0.08)', borderRadius: 1, border: '1px solid rgba(220,38,38,0.20)' }}>
                          <WarningAmber sx={{ color: '#991B1B', fontSize: 16 }} />
                          <Typography sx={{ fontSize: '0.74rem', color: '#991B1B', fontWeight: 700 }}>
                            This date is in the past — please verify.
                          </Typography>
                        </Stack>
                      ) : isExpiringSoon(expirationDate) ? (
                        <Stack direction="row" alignItems="center" spacing={0.75}
                          sx={{ p: 0.75, bgcolor: 'rgba(217,119,6,0.10)', borderRadius: 1, border: '1px solid rgba(217,119,6,0.25)' }}>
                          <WarningAmber sx={{ color: '#A16207', fontSize: 16 }} />
                          <Typography sx={{ fontSize: '0.74rem', color: '#A16207', fontWeight: 700 }}>
                            Expires within 90 days — short shelf life.
                          </Typography>
                        </Stack>
                      ) : (
                        <Stack direction="row" alignItems="center" spacing={0.75}
                          sx={{ p: 0.75, bgcolor: 'rgba(16,185,129,0.10)', borderRadius: 1, border: '1px solid rgba(16,185,129,0.25)' }}>
                          <CheckCircle sx={{ color: '#15803D', fontSize: 16 }} />
                          <Typography sx={{ fontSize: '0.74rem', color: '#15803D', fontWeight: 700 }}>
                            {Math.round((new Date(expirationDate) - new Date()) / 86400000)} days until expiry.
                          </Typography>
                        </Stack>
                      )}
                    </Box>
                  )}
                </>
              ) : (
                <Box sx={{ py: 2.5, textAlign: 'center' }}>
                  <ShoppingCartOutlined sx={{ fontSize: 30, color: '#cbd5e1', mb: 0.5 }} />
                  <Typography sx={{ fontSize: '0.84rem', fontWeight: 600, color: STRONG }}>
                    Pick a product on the left to start
                  </Typography>
                  <Typography sx={{ fontSize: '0.74rem', color: MUTED, mt: 0.25 }}>
                    The form will activate once a product is selected.
                  </Typography>
                </Box>
              )}
            </Paper>

            {/* Invoice items */}
            <Paper elevation={0} sx={{
              flexGrow: 1, borderRadius: 2, border: `1px solid ${BORDER}`, overflow: 'hidden',
              display: 'flex', flexDirection: 'column', bgcolor: '#fff',
            }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center"
                sx={{ px: 1.75, py: 1.1, bgcolor: '#f8fafc', borderBottom: `1px solid ${BORDER}` }}>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <ReceiptLong sx={{ fontSize: 16, color: PRIMARY }} />
                  <Typography sx={LABEL_SX}>Invoice Items</Typography>
                </Stack>
                <Box sx={{
                  px: 1, py: 0.25, borderRadius: 1, bgcolor: PRIMARY_BG, color: PRIMARY,
                  fontSize: '0.7rem', fontWeight: 700,
                }}>
                  {purchaseItems.length} line{purchaseItems.length !== 1 ? 's' : ''}
                </Box>
              </Stack>

              <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                {purchaseItems.length === 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 200, color: '#94a3b8', p: 3 }}>
                    <ShoppingCartOutlined sx={{ fontSize: 38, mb: 1 }} />
                    <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: STRONG }}>No items on this invoice yet</Typography>
                    <Typography sx={{ fontSize: '0.74rem', color: MUTED, mt: 0.25 }}>
                      Pick a product, fill quantity / cost / expiry, then press Enter.
                    </Typography>
                  </Box>
                ) : (
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ '& th': { bgcolor: '#fff', borderBottom: `1px solid ${BORDER}`, ...LABEL_SX, py: 1 } }}>
                        <TableCell sx={{ width: 40 }}>#</TableCell>
                        <TableCell>Product</TableCell>
                        <TableCell align="right" sx={{ width: 60 }}>Qty</TableCell>
                        <TableCell align="right" sx={{ width: 100 }}>Unit</TableCell>
                        <TableCell align="center" sx={{ width: 150 }}>Expiry</TableCell>
                        <TableCell align="right" sx={{ width: 110 }}>Total</TableCell>
                        <TableCell align="center" sx={{ width: 50 }}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {purchaseItems.map((item, index) => {
                        const exp = expiryStatus(item.expirationDate);
                        return (
                          <TableRow key={`${item.code || item.name}-${index}`}
                            sx={{ '&:hover': { bgcolor: '#f8fafc' }, borderBottom: `1px solid ${BORDER}` }}>
                            <TableCell sx={{ color: MUTED, fontWeight: 700, fontSize: '0.78rem' }}>{index + 1}</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: STRONG, fontSize: '0.84rem' }}>{item.name}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', fontSize: '0.84rem' }}>
                              {item.purchaseQuantity}
                            </TableCell>
                            <TableCell align="right" sx={{ color: MUTED, fontVariantNumeric: 'tabular-nums', fontSize: '0.82rem' }}>
                              {formatCurrency(item.purchaseCost)}
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{
                                display: 'inline-flex', alignItems: 'center', gap: 0.5,
                                px: 0.85, py: 0.2, borderRadius: 0.85,
                                bgcolor: exp.bg, color: exp.color,
                                fontSize: '0.66rem', fontWeight: 700,
                              }}>
                                {exp.label}
                              </Box>
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800, color: PRIMARY, fontVariantNumeric: 'tabular-nums', fontSize: '0.85rem' }}>
                              {formatCurrency(item.totalCost)}
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="Remove" arrow>
                                <IconButton size="small" onClick={() => handleRemovePurchaseItem(index)}
                                  sx={{ color: '#dc2626', '&:hover': { bgcolor: 'rgba(220,38,38,0.10)' } }}>
                                  <DeleteOutline sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </Box>

              {/* Totals strip */}
              <Box sx={{ borderTop: `1px solid ${BORDER}`, bgcolor: '#f8fafc', px: 2, py: 1.25 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" spacing={3}>
                    <Box>
                      <Typography sx={LABEL_SX}>Lines</Typography>
                      <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: STRONG, lineHeight: 1.1 }}>
                        {purchaseItems.length}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={LABEL_SX}>Total Units</Typography>
                      <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: STRONG, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
                        {totalUnits.toLocaleString()}
                      </Typography>
                    </Box>
                  </Stack>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography sx={LABEL_SX}>Grand Total</Typography>
                    <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: PRIMARY, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
                      {formatCurrency(grandTotal)}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Paper>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 1.5, bgcolor: '#fff', borderTop: `1px solid ${BORDER}` }}>
          <Typography sx={{ fontSize: '0.78rem', color: MUTED, mr: 'auto', pl: 1.5 }}>
            {purchaseItems.length === 0
              ? 'Add at least one item to save this invoice.'
              : `Ready — ${purchaseItems.length} line${purchaseItems.length !== 1 ? 's' : ''}, ${formatCurrency(grandTotal)} total.`}
          </Typography>
          <Button onClick={handleClosePurchaseDialog} disabled={savingPurchase}
            sx={{ textTransform: 'none', color: MUTED, fontWeight: 600, px: 2.5, '&:hover': { bgcolor: '#f1f5f9' } }}>
            Cancel
          </Button>
          <Button
            onClick={handleSavePurchase} variant="contained"
            disabled={purchaseItems.length === 0 || savingPurchase}
            startIcon={savingPurchase ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <CheckCircle sx={{ fontSize: 18 }} />}
            sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: PRIMARY_DARK }, textTransform: 'none', fontWeight: 700, px: 3 }}
          >
            {savingPurchase ? 'Processing…' : 'Save Invoice'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PurchaseOrders;
